import { useEffect, useState } from 'react';
import { ktmToday } from '../../utils/calendar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import DualDatePicker from '../../components/common/DualDatePicker';
import BeltBadge from '../../components/common/BeltBadge';
import { MdEdit, MdSave, MdClose, MdPerson, MdHistory, MdContentCopy } from 'react-icons/md';
import api from '../../services/api';
import { useCalendar } from '../../context/CalendarContext';
import DualDate from '../../components/common/DualDate';
import { getDisplayTxId } from '../../utils/displayId';
import { sanitizePhone, isValidPhone, PHONE_HINT } from '../../utils/inputRules';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function MyProfile() {
  const { formatDate } = useCalendar();
  document.title = 'My Profile — TrainAxis';
  const queryClient          = useQueryClient();
  const [editing, setEditing] = useState(false);
  const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm();

  const { data, isPending } = useQuery({
    queryKey: ['student-me'],
    queryFn:  () => api.get('/api/students/me').then(r => r.data),
  });

  const profile = data?.profile;

  useEffect(() => {
    if (profile && editing) {
      reset({
        dateOfBirth:                    profile.dateOfBirth ? String(profile.dateOfBirth).split('T')[0] : '',
        gender:                         profile.gender || '',
        phone:                          profile.phone || '',
        'address.street':               profile.address?.street || '',
        'address.city':                 profile.address?.city || '',
        'address.district':             profile.address?.district || '',
        'emergencyContact.name':        profile.emergencyContact?.name || '',
        'emergencyContact.phone':       profile.emergencyContact?.phone || '',
        'emergencyContact.relationship':profile.emergencyContact?.relationship || '',
      });
    }
  }, [profile, editing, reset]);

  const mutation = useMutation({
    mutationFn: (data) => api.put('/api/students/me', {
      dateOfBirth: data.dateOfBirth || undefined,
      gender: data.gender || undefined,
      phone: data.phone,
      address: {
        street:   data['address.street'],
        city:     data['address.city'],
        district: data['address.district'],
      },
      emergencyContact: {
        name:         data['emergencyContact.name'],
        phone:        data['emergencyContact.phone'],
        relationship: data['emergencyContact.relationship'],
      },
    }),
    onSuccess: () => {
      toast.success('Profile updated');
      queryClient.invalidateQueries({ queryKey: ['student-me'] });
      setEditing(false);
    },
    onError: () => toast.error('Failed to update profile'),
  });

  if (isPending) return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-2xl" />)}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header card */}
      <Card>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
            {(profile?.user?.name || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-secondary">{profile?.user?.name}</h2>
            <p className="text-sm text-gray-400">{profile?.user?.email}</p>
            <div className="mt-2">
              <BeltBadge belt={profile?.currentGrade || 'White'} size="sm" />
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Joined</p>
            <p className="font-semibold text-secondary text-sm"><DualDate date={profile?.joinDate} /></p>
            <p className="text-xs text-gray-400 mt-1">{profile?.discipline}</p>
            {getDisplayTxId(profile) !== 'ID pending' && (
              <div className="mt-2">
                <p className="text-[10px] text-gray-400 mb-1">Your TrainAxis ID</p>
                <button
                  type="button"
                  title="Click to copy"
                  onClick={() => {
                    navigator.clipboard.writeText(getDisplayTxId(profile)).catch(() => {});
                    toast.success('ID copied!', { duration: 1500 });
                  }}
                  className="flex items-center gap-1.5 font-mono text-sm font-bold text-primary bg-red-500/10 px-3 py-1 rounded-lg transition-colors duration-150 hover:bg-red-500/20"
                >
                  {getDisplayTxId(profile)}
                  <MdContentCopy className="text-xs opacity-60" />
                </button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Personal Details */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-secondary text-sm flex items-center gap-2">
            <MdPerson className="text-primary" /> Personal Details
          </h3>
          {!editing ? (
            <Button variant="ghost" onClick={() => setEditing(true)} className="text-xs">
              <MdEdit /> Edit
            </Button>
          ) : (
            <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600">
              <MdClose />
            </button>
          )}
        </div>

        {!editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            {[
              ['Phone',        profile?.phone],
              ['Street',       profile?.address?.street],
              ['City',         profile?.address?.city],
              ['District',     profile?.address?.district],
              ['Emergency Contact', profile?.emergencyContact?.name],
              ['Emergency Phone',   profile?.emergencyContact?.phone],
              ['Relationship',      profile?.emergencyContact?.relationship],
            ].map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-gray-400">{label}</p>
                <p className="font-medium text-secondary">{value || '—'}</p>
              </div>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date of Birth</label>
              <Controller name="dateOfBirth" control={control}
                render={({ field }) => (
                  <DualDatePicker value={field.value || ''} onChange={field.onChange} max={ktmToday()} fastNav />
                )} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
                <select {...register('gender')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-primary">
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <Input label="Phone" error={errors.phone?.message}
                {...register('phone', { validate: (v) => isValidPhone(v) || PHONE_HINT })}
                onChange={(e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true })} />
              <Input label="Street" {...register('address.street')} />
              <Input label="City" {...register('address.city')} />
              <Input label="District" {...register('address.district')} />
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Emergency Contact</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input label="Name" {...register('emergencyContact.name')} />
              <Input label="Phone" error={errors.emergencyContact?.phone?.message}
                {...register('emergencyContact.phone', { validate: (v) => isValidPhone(v) || PHONE_HINT })}
                onChange={(e) => setValue('emergencyContact.phone', sanitizePhone(e.target.value), { shouldValidate: true })} />
              <Input label="Relationship" placeholder="Father, Mother…" {...register('emergencyContact.relationship')} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={mutation.isPending}>
                <MdSave /> {mutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </form>
        )}
      </Card>

      {/* Belt History */}
      <Card>
        <h3 className="font-semibold text-secondary text-sm flex items-center gap-2 mb-4">
          <MdHistory className="text-primary" /> Belt Grading History
        </h3>
        {!profile?.gradingHistory?.length ? (
          <p className="text-gray-400 text-sm text-center py-6">No grading history yet</p>
        ) : (
          <div className="relative pl-4">
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-100" />
            {profile.gradingHistory.map((g, i) => (
              <div key={i} className="relative mb-4 last:mb-0">
                <div className={`absolute -left-1.5 w-3 h-3 rounded-full border-2 border-white ${g.passed ? 'bg-green-500' : 'bg-red-400'}`} />
                <div className="ml-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <BeltBadge belt={g.fromGrade} size="xs" />
                    <span className="text-xs text-gray-400">→</span>
                    <BeltBadge belt={g.toGrade} size="xs" />
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${g.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {g.passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1"><DualDate date={g.date} /> {g.venue ? `· ${g.venue}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default MyProfile;
